import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '@/components/ui/Input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/alert';
import { Plus, Edit, Trash2, Save, RotateCcw, Loader2, BarChart3, Zap } from 'lucide-react';
import { useTenant } from '@/lib/tenant/tenant-provider';
import {
  listPerformanceParameters,
  createPerformanceParameter,
  updatePerformanceParameter
} from '@/api/performance';
import type {
  PerformanceParameter,
  CreatePerformanceParameterData,
  UpdatePerformanceParameterData
} from '@/types/performance';

const ParametrosPerformance = () => {
  const { tenantDomain } = useTenant();
  const [parametros, setParametros] = useState<PerformanceParameter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingParametro, setEditingParametro] = useState<PerformanceParameter | null>(null);
  const [novoParametro, setNovoParametro] = useState<Partial<CreatePerformanceParameterData>>({
    name: '',
    minScore: 0,
    maxScore: 100,
    color: '#3B82F6',
    description: '',
    order: 1,
    status: 'active'
  });

  const coresDisponiveis = [
    { value: '#22C55E', label: 'Verde', class: 'bg-success' },
    { value: '#EAB308', label: 'Amarelo', class: 'bg-warning' },
    { value: '#F97316', label: 'Laranja', class: 'bg-orange-500' },
    { value: '#EF4444', label: 'Vermelho', class: 'bg-error' },
    { value: '#3B82F6', label: 'Azul', class: 'bg-brand-blue' },
    { value: '#A855F7', label: 'Roxo', class: 'bg-brand-primary' },
    { value: '#6B7280', label: 'Cinza', class: 'bg-brand-gray/30' }
  ];

  // Fetch parameters on component mount
  const fetchParametros = async () => {
    if (!tenantDomain) return;

    setLoading(true);
    setError(null);
    try {
      const result = await listPerformanceParameters(tenantDomain);
      setParametros(result.items);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar parâmetros';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParametros();
  }, [tenantDomain]);

  // Auto-hide success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEditParametro = (parametro: PerformanceParameter) => {
    setEditingParametro(parametro);
    setNovoParametro({
      name: parametro.name,
      minScore: parametro.minScore,
      maxScore: parametro.maxScore,
      color: parametro.color,
      description: parametro.description,
      order: parametro.order,
      status: parametro.status
    });
    setIsEditDialogOpen(true);
  };

  const handleAddParametro = () => {
    setEditingParametro(null);
    setNovoParametro({
      name: '',
      minScore: 0,
      maxScore: 100,
      color: '#3B82F6',
      description: '',
      order: parametros.length + 1,
      status: 'active'
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveParametro = async () => {
    if (!tenantDomain) {
      setError('Tenant domain não disponível');
      return;
    }

    // Validação
    if (!novoParametro.name?.trim() || !novoParametro.description?.trim()) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if ((novoParametro.minScore ?? 0) >= (novoParametro.maxScore ?? 100)) {
      setError('O limite mínimo deve ser menor que o limite máximo.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (editingParametro) {
        // Update existing parameter
        const updates: UpdatePerformanceParameterData = {
          name: novoParametro.name,
          minScore: novoParametro.minScore,
          maxScore: novoParametro.maxScore,
          color: novoParametro.color,
          description: novoParametro.description,
          order: novoParametro.order,
          status: novoParametro.status
        };
        await updatePerformanceParameter(
          tenantDomain,
          editingParametro.parameterId,
          updates
        );
        setSuccessMessage('Parâmetro atualizado com sucesso');
      } else {
        // Create new parameter
        const data: CreatePerformanceParameterData = {
          name: novoParametro.name!,
          minScore: novoParametro.minScore ?? 0,
          maxScore: novoParametro.maxScore ?? 100,
          color: novoParametro.color ?? '#3B82F6',
          description: novoParametro.description!,
          order: novoParametro.order ?? 1,
          status: novoParametro.status ?? 'active'
        };
        await createPerformanceParameter(tenantDomain, data);
        setSuccessMessage('Parâmetro criado com sucesso');
      }

      setIsEditDialogOpen(false);
      await fetchParametros(); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao salvar parâmetro';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getColorStyle = (hexColor: string) => {
    const brightness = parseInt(hexColor.slice(1, 3), 16) * 0.299 +
                      parseInt(hexColor.slice(3, 5), 16) * 0.587 +
                      parseInt(hexColor.slice(5, 7), 16) * 0.114;
    const textColor = brightness > 128 ? '#000000' : '#FFFFFF';
    return { backgroundColor: hexColor, color: textColor };
  };

  const verificarSobreposicao = () => {
    const sobreposicoes: string[] = [];
    for (let i = 0; i < parametros.length; i++) {
      for (let j = i + 1; j < parametros.length; j++) {
        const param1 = parametros[i];
        const param2 = parametros[j];

        if ((param1.minScore <= param2.maxScore && param1.maxScore >= param2.minScore)) {
          sobreposicoes.push(`${param1.name} e ${param2.name}`);
        }
      }
    }
    return sobreposicoes;
  };

  const sobreposicoes = verificarSobreposicao();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gray" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-bold">Parâmetros de Performance</h2>
          <p className="text-brand-gray">Configure as faixas de avaliação e legendas de performance</p>
        </div>

        <div className="flex gap-2">
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleAddParametro}
                className="bg-success text-black hover:bg-success"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Parâmetro
              </Button>
            </DialogTrigger>

            <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingParametro ? 'Editar Parâmetro' : 'Adicionar Novo Parâmetro'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={novoParametro.name}
                    onChange={(e) => setNovoParametro({...novoParametro, name: e.target.value})}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                    placeholder="Ex: Alta Performance"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="minScore">Pontuação Mínima</Label>
                    <Input
                      id="minScore"
                      type="number"
                      min="0"
                      max="100"
                      value={novoParametro.minScore}
                      onChange={(e) => setNovoParametro({...novoParametro, minScore: parseInt(e.target.value) || 0})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="maxScore">Pontuação Máxima</Label>
                    <Input
                      id="maxScore"
                      type="number"
                      min="0"
                      max="100"
                      value={novoParametro.maxScore}
                      onChange={(e) => setNovoParametro({...novoParametro, maxScore: parseInt(e.target.value) || 0})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="order">Ordem de Exibição</Label>
                  <Input
                    id="order"
                    type="number"
                    min="1"
                    value={novoParametro.order}
                    onChange={(e) => setNovoParametro({...novoParametro, order: parseInt(e.target.value) || 1})}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                  />
                </div>

                <div>
                  <Label>Cor da Legenda</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {coresDisponiveis.map(cor => (
                      <button
                        key={cor.value}
                        type="button"
                        onClick={() => setNovoParametro({...novoParametro, color: cor.value})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          novoParametro.color === cor.value
                            ? 'border-white'
                            : 'border-brand-gray/20 hover:border-brand-gray/20'
                        } ${cor.class}`}
                        title={cor.label}
                      >
                        <span className="text-xs text-white mix-blend-difference">
                          {cor.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Descrição *</Label>
                  <textarea
                    id="description"
                    value={novoParametro.description}
                    onChange={(e) => setNovoParametro({...novoParametro, description: e.target.value})}
                    className="w-full p-2 bg-brand-dark-lighter border border-brand-gray/20 text-white rounded-md resize-none"
                    rows={3}
                    placeholder="Descreva os critérios para esta faixa de performance..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                    disabled={submitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveParametro}
                    className="bg-success text-black hover:bg-success"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salvar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="bg-error/20/20 border-error">
          <div className="flex items-center gap-2">
            <span className="text-error"></span>
            <div>
              <p className="text-error font-medium">Erro</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert className="bg-success/20/20 border-success">
          <div className="flex items-center gap-2">
            <span className="text-success"></span>
            <div>
              <p className="text-success font-medium">Sucesso</p>
              <p className="text-green-300 text-sm">{successMessage}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Overlap Warning */}
      {sobreposicoes.length > 0 && (
        <Card className="bg-error/20/20 border-error">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <span className="text-error"></span>
              <div>
                <p className="text-error font-medium">Sobreposição de Faixas Detectada</p>
                <p className="text-red-300 text-sm">
                  As seguintes faixas possuem sobreposição: {sobreposicoes.join(', ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parameters Table */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardHeader>
          <CardTitle className="text-white">Configuração de Parâmetros</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-brand-gray/10">
                <TableHead className="text-white">Nome</TableHead>
                <TableHead className="text-white text-center">Faixa de Pontuação</TableHead>
                <TableHead className="text-white text-center">Ordem</TableHead>
                <TableHead className="text-white text-center">Cor</TableHead>
                <TableHead className="text-white">Descrição</TableHead>
                <TableHead className="text-white text-center">Status</TableHead>
                <TableHead className="text-white text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parametros.sort((a, b) => a.order - b.order).map((param) => (
                <TableRow key={param.parameterId} className="border-brand-gray/10">
                  <TableCell className="text-white font-medium">{param.name}</TableCell>
                  <TableCell className="text-center">
                    <span className="text-white">
                      {param.minScore} - {param.maxScore}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-white">{param.order}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge style={getColorStyle(param.color)}>
                      {param.name}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-white max-w-xs">
                    <div className="truncate" title={param.description}>
                      {param.description}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={param.status === 'active' ? 'success' : 'default'}>
                      {param.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditParametro(param)}
                        className="text-brand-gray hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {parametros.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              {/* Icon Container */}
              <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="h-8 w-8 text-success" />
              </div>

              {/* Heading */}
              <h3 className="text-white text-xl font-display mb-3">
                Defina faixas de performance
              </h3>

              {/* Supporting Text */}
              <p className="text-brand-gray text-center max-w-2xl mb-8">
                Os parâmetros classificam os resultados das avaliações em faixas (ex: Alta Performance, Adequado, Precisa Melhorar).
                Configure os limites de pontuação e cores de cada faixa para gerar relatórios visuais precisos.
              </p>

              {/* Example Visual Box */}
              <div className="bg-brand-dark/50 border border-brand-gray/20 rounded-lg p-6 max-w-2xl w-full mb-6">
                <h4 className="text-white font-medium mb-4">Exemplo de configuração:</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-success rounded"></div>
                    <span className="text-white">Alta Performance: 80-100</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-brand-blue rounded"></div>
                    <span className="text-white">Adequado: 60-79</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-warning rounded"></div>
                    <span className="text-white">Precisa Melhorar: 40-59</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-error rounded"></div>
                    <span className="text-white">Crítico: 0-39</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddParametro}
                  className="bg-success text-black hover:bg-success"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Faixa
                </Button>
                <Button
                  variant="outline"
                  className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Usar Configuração Padrão
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visual Representation */}
      {parametros.length > 0 && (
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <CardTitle className="text-white">Visualização das Faixas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex h-8 rounded-lg overflow-hidden">
                {parametros
                  .sort((a, b) => a.minScore - b.minScore)
                  .map((param) => {
                    const width = param.maxScore - param.minScore + 1;
                    return (
                      <div
                        key={param.parameterId}
                        className="flex items-center justify-center text-xs font-medium transition-all hover:opacity-80"
                        style={{
                          ...getColorStyle(param.color),
                          width: `${width}%`,
                          minWidth: '60px'
                        }}
                        title={`${param.name}: ${param.minScore} - ${param.maxScore}`}
                      >
                        {width > 15 && param.name}
                      </div>
                    );
                  })}
              </div>

              <div className="flex justify-between text-brand-gray text-xs">
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParametrosPerformance;
